import React from "react";
import { ChevronDown, Sparkles } from 'lucide-react';

type ModelMeta = {
  label: string;                // UI label shown on the button
  origin: string;               // Company / project
  updatedAt: string;            // e.g. "2025-09-30"
  openness: "proprietary" | "open-source" | "open-weights";
  licenseNote?: string;         // Optional note about license
  docsUrl?: string;             // Optional link to docs
  shortBlurb: string;           // Tiny one-liner for tooltip
  whenToUse: string[];          // Bullets
  whenNotToUse: string[];       // Bullets
};

const MODEL_META: Record<EditModel, ModelMeta> = {
  google: {
    label: "Google Nano Banana 2 (Flash)",
    origin: "Google",
    updatedAt: "2026-02-26",
    openness: "proprietary",
    licenseNote: "Subject to Google Cloud's Generative AI Model Terms.",
    shortBlurb: "The go-to model for virtually all tasks — fast, versatile, and handles everything from text to complex edits. (Note: This is a fun codename for Google's 'Gemini 3.1 Flash Image'.)",
    docsUrl: "https://ai.google.dev/gemini-api/docs/image-generation",
    whenToUse: [
      "Your default choice for almost every task — best balance of speed, quality, and cost",
      "Making sure multiple characters (up to 5) or objects (up to 14) remain consistent across edits",
      "Rendering accurate, legible text within an image",
      "Generating production-ready assets with full control over aspect ratios",
      "Multi-turn conversational editing that preserves visual context between turns",
      "When you need precise instruction following for complex requests and nuances"
    ],
    whenNotToUse: [
      "For projects that are only allowed to use 'open-source' (publicly shared) tools"
    ]
  },
  'google-pro': {
    label: "Google Nano Banana (Pro)",
    origin: "Google",
    updatedAt: "2025-11-20",
    openness: "proprietary",
    licenseNote: "Subject to Google Cloud's Generative AI Model Terms.",
    shortBlurb: "Premium model for complex multi-element compositions. Most tasks don't need this — start with Flash.",
    docsUrl: "https://ai.google.dev/gemini-api/docs/image-generation",
    whenToUse: [
      "Complex multi-element compositions requiring spatial reasoning (e.g., 'move the cat to the sofa and add a realistic shadow')",
      "Scenes where the built-in Thinking process helps plan composition before generating",
      "Editing highly detailed scenes where lighting and physics consistency matter",
    ],
    whenNotToUse: [
      "For text rendering or standard edits — Flash handles those equally well and is much faster",
      "For extensions and reformats — Flash is the better default",
      "When you need fast response times (Flash is significantly faster)",
      "For projects that are only allowed to use 'open-source' tools",
    ],
  },
  openai: {
    label: "ChatGPT Image 2.5",
    origin: "OpenAI",
    updatedAt: "2026-09-08",
    openness: "proprietary",
    licenseNote: "Subject to OpenAI's usage policies and API terms.",
    shortBlurb: "OpenAI's gpt-image-2.5 Flare — alternative rendering style, strong typography and photorealism. Useful when Gemini output looks off.",
    docsUrl: "https://platform.openai.com/docs/guides/image-generation",
    whenToUse: [
      "Photographic scenes where you want an alternative style to Gemini",
      "Text-heavy images when Gemini misrenders typography",
      "When you want a second opinion and Gemini's result isn't quite right",
    ],
    whenNotToUse: [
      "Your first attempt — start with Google Flash, it's faster and cheaper",
      "Projects that must use 'open-source' tools",
      "When you need 4K output (OpenAI caps at 1536px per side)",
    ],
  },
};

type Props = {
  isLoading: boolean;
  editModel: EditModel;
  setEditModel: (m: EditModel) => void;
};

export const ModelChoice: React.FC<Props> = ({ isLoading, editModel, setEditModel }) => {
  const models: EditModel[] = ["google", "google-pro", "openai"];
  const [isExpanded, setIsExpanded] = React.useState(false);

  const expandedDiv = (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-gray-400">Model:</span>

        {models.map((model, index) => {
          const meta = MODEL_META[model];
          const active = editModel === model;
          const isLast = index === models.length - 1;

          return (
            <div key={model} className="relative group">
              <button
                onClick={() => setEditModel(model)}
                disabled={isLoading}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 active:scale-95 disabled:opacity-50 ${
                  active
                    ? 'bg-gradient-to-br from-blue-600 to-blue-500 text-white shadow-md shadow-blue-500/20'
                    : 'bg-white/10 hover:bg-white/20 text-gray-200'
                }`}
              >
                {meta.label}
              </button>
        
              {/* DETAILED TOOLTIP */}
              <div className={`absolute top-full z-100 mt-2 hidden w-[320px] rounded-xl border border-white/10 bg-gray-900/95 p-3 text-sm text-gray-200 shadow-2xl group-hover:block ${
                isLast ? 'right-0' : 'left-1/2 -translate-x-1/2'
              }`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold text-white">{meta.origin}</div>
                    <div className="text-gray-300 text-xs">
                      Updated: {meta.updatedAt} • {meta.openness}
                    </div>
                  </div>
                </div>
        
                <p className="mt-2 text-gray-300 text-xs">{meta.licenseNote}</p>
        
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <div>
                    <div className="mb-1 text-xs font-semibold text-gray-100">When to use</div>
                    <ul className="list-disc pl-4 space-y-0.5 text-xs text-gray-300">
                      {meta.whenToUse.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-semibold text-gray-100">When not to use</div>
                    <ul className="list-disc pl-4 space-y-0.5 text-xs text-gray-300">
                      {meta.whenNotToUse.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  )


  return (
    <div className="relative z-50 bg-gray-800/40 backdrop-blur-xl border border-gray-700/50 rounded-xl shadow-lg">
      <button
        onClick={() => setIsExpanded(state => !state)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-700/20 transition"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-blue-400" />
          <h3 className="text-sm font-semibold text-gray-200">Model Settings</h3>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      
      {isExpanded && (
        <div className="px-4 pb-4 pt-3 space-y-3">
          <div>
            {expandedDiv}
          </div>
        </div>
      )}
    </div>
  )
};

export default ModelChoice;
